import { useEffect, useState } from "react";
import ApplicationFormFooter from "../../pages/Application/ApplicationFormFooter";
import EvaluationFormHeader from "../../pages/EvaluationNew/components/EvaluationFormHeader";
import EvaluationInput from "../../pages/EvaluationNew/components/EvaluationInput";
import InputDatePicker from "../../pages/EvaluationNew/components/InputDatePicker";
import { request } from "../../utils/api";
import { useApplicationFormLogic } from '../../hooks/useApplicationFormLogic';
import toast from "react-hot-toast";
import { FormProvider, useForm } from "react-hook-form";
import CorrectionRequestAlert from "./CorrectionRequestAlert";
import { getLocalSystemDate, toIsoDate, parseIsoDateToMDYUS } from "../../utils/dateUtils";
import { mergeFormDataWithStatusExtras } from "../../utils/applicationFormPayload";
import useCurrentUser from '../../hooks/useCurrentUser';
import { competencyForPositionLabel } from '../../pages/Application/competencyPositionTitle';

const personFullName = (person) => {
    if (!person) return "";
    const joined = [person.firstName, person.lastName].filter(Boolean).join(" ").trim();
    return joined || person.fullName || person.name || "";
};

const BaseCompetency = ({
    role,
    items, // The structured constant
    totalPages = 3,
    apiEndpoint = "/competencies",
    defaultValues = {},
    onClose,
}) => {

    const [page, setPage] = useState(1);
    const { user: currentUserData, fullName, hireDate, isAdminRole: isAdmin,tenantId } = useCurrentUser()

    // Process items to build the form's initial sections
    // Each 'section' in 'items' might have multiple 'competencies' (categories)
    // We want to map each CATEGORY to a section in the API payload for better organization
    // Process items to build the form's initial sections
    const initialSections = [];
    items.forEach((item) => {
        if (item.type === "section" && item.competencies) {
            item.competencies.forEach((comp) => {
                const sectionItems = [];
                const rawItems = comp.items || [];

                if (rawItems.length === 0 && comp.category) {
                    // If no items but has category, treat category as the item
                    rawItems.push(comp.category);
                }

                rawItems.forEach((subItem) => {
                    const text =
                        typeof subItem === "string" ? subItem : subItem.text || "";
                    const isProficiencyRequired =
                        typeof subItem === "object" && subItem.proficiencyRequired;
                    const method = typeof subItem === "object" ? subItem.method : "";

                    sectionItems.push({
                        skill: text,
                        competency: text,
                        method: method,
                        hhaSelfRating: "",
                        supervisorCompetencyMet: false,
                        supervisorCompetencyNotMet: false,
                        evaluatorInitials: "",
                        evaluatorDate: parseIsoDateToMDYUS(getLocalSystemDate()),
                        hasExperience: "",
                        isCompetent: "",
                        proficiencyRequired: isProficiencyRequired || false,
                        evaluationMethod: method || "",
                        preceptorInitials: "",
                        preceptorDate: parseIsoDateToMDYUS(getLocalSystemDate()),
                    });

                    // Flatten subItems if they exist
                    if (typeof subItem === "object" && subItem.subItems) {
                        subItem.subItems.forEach((nestedItem) => {
                            const nestedText =
                                typeof nestedItem === "string"
                                    ? nestedItem
                                    : nestedItem.text || "";
                            sectionItems.push({
                                skill: nestedText,
                                competency: nestedText,
                                method: method, // Inherit method if applicable
                                hhaSelfRating: "",
                                supervisorCompetencyMet: "",
                                supervisorCompetencyNotMet: "",
                                evaluatorInitials: "",
                                evaluatorDate: parseIsoDateToMDYUS(getLocalSystemDate()),
                                hasExperience: "",
                                isCompetent: "",
                                proficiencyRequired: false,
                                evaluationMethod: method || "",
                                preceptorInitials: "",
                                preceptorDate: parseIsoDateToMDYUS(getLocalSystemDate()),
                            });
                        });
                    }
                });

                initialSections.push({
                    sectionTitle: comp.category || item.title,
                    items: sectionItems,
                });
            });
        }
    });

    const methods = useForm({
        defaultValues: {
            tenantId: 0,
            userId: undefined,
            applicantName: fullName || "",
            name: fullName || "",
            dateOfEmployment: hireDate || "",
            dateCompleted: parseIsoDateToMDYUS(getLocalSystemDate()),
            sections: initialSections,
            applicantSignature: "",
            applicantSignedBy: "",
            applicantSignedAt: parseIsoDateToMDYUS(getLocalSystemDate()),
            reviewedBySignature: "",
            reviewedBySignedAt: "",
            reviewedBySignedBy: "",
            applicantStatementAccepted: false,
            ...defaultValues,
        },
        mode: "onChange",
    });

    const { watch } = methods;

    const [currentStatus, setCurrentStatus] = useState("");
    const [dataId, setDataId] = useState(null);

    const {
        targetUserId,
        isSignatureModalOpen,
        openSignatureModal,
        closeSignatureModal,
        handleSignatureApply,
        handleStatusAction,
        handleSave,
        signerName,
        isReadOnly,
        closeForm,
        actualFromId
    } = useApplicationFormLogic({
        methods,
        // Keep Employee Name (`name`) stable; do not overwrite it with the signer.
        applicantNameField: "",
        applicantSignatureDateField: "applicantSignedAt",
        onClose,
        onSubmit: (data, flag, additionalData) => onSubmit(data, flag, additionalData),
        currentStatus
    });

    useEffect(() => {
        const fetchData = async () => {
            if (!currentUserData) return;

            // Blank copy when opening without a record id (Add New Form).
            if (!actualFromId && !dataId) {
                const ownerId = Number(targetUserId || currentUserData.id);
                let employeeName = fullName || "";
                let employmentDate = hireDate;
                if (ownerId && ownerId !== Number(currentUserData.id)) {
                    try {
                        const userRes = await request(`/users/${ownerId}`);
                        const owner =
                            userRes.result?.user || userRes.result?.data || userRes.result;
                        employeeName = personFullName(owner) || employeeName;
                        employmentDate = owner?.hireDate || employmentDate;
                    } catch (err) {
                        console.error("Error loading competency owner:", err);
                    }
                }
                methods.reset({
                    tenantId: tenantId || 0,
                    userId: ownerId,
                    applicantName: employeeName,
                    name: employeeName,
                    dateOfEmployment: parseIsoDateToMDYUS(employmentDate) || "",
                    dateCompleted: parseIsoDateToMDYUS(getLocalSystemDate()),
                    applicantSignedAt: parseIsoDateToMDYUS(getLocalSystemDate()),
                    applicantStatementAccepted: false,
                    sections: initialSections,
                });
                setCurrentStatus("");
                return;
            }

            if (!targetUserId) return;

            try {
                const isAdminUser = Number(currentUserData?.roleId) === 1 || Number(currentUserData?.role?.id) === 1;
                let response;
                if (isAdminUser) {
                    response = await request(`${apiEndpoint}/${actualFromId}?userId=${targetUserId}`);
                } else {
                    response = await request(`${apiEndpoint}/${actualFromId}`);
                }

                if (response.status === 1) {
                    const data = response.result?.competency || response.result?.data || response.result;

                    setDataId(data.id);
                    setCurrentStatus(data.status || "");

                    const fetchedSections = data.competencyData?.sections;

                    // Remap fetched sections back into the form's shape.
                    // The form stores `skill` (same as `competency`) and radio values as strings.
                    const remappedSections = fetchedSections
                        ? fetchedSections.map((section) => ({
                            sectionTitle: section.sectionTitle,
                            items: section.items.map((item) => ({
                                skill: item.competency || item.skill || "",
                                competency: item.competency || item.skill || "",
                                method: item.method || "",
                                // Radio inputs bind to string "true"/"false"/"" — convert booleans
                                hasExperience:
                                    item.hasExperience === true
                                        ? "true"
                                        : item.hasExperience === false
                                            ? "false"
                                            : "",
                                isCompetent:
                                    item.isCompetent === true
                                        ? "true"
                                        : item.isCompetent === false
                                            ? "false"
                                            : "",
                                proficiencyRequired: item.proficiencyRequired || false,
                                evaluationMethod: item.evaluationMethod || "",
                                preceptorInitials: item.preceptorInitials || "",
                                preceptorDate: parseIsoDateToMDYUS(item.preceptorDate),
                                // HHA-specific fields
                                hhaSelfRating: item.hhaSelfRating || "",
                                supervisorCompetencyMet:
                                    item.supervisorCompetencyMet === true
                                        ? "true"
                                        : item.supervisorCompetencyMet === false
                                            ? "false"
                                            : "",
                                supervisorCompetencyNotMet:
                                    item.supervisorCompetencyNotMet === true
                                        ? "true"
                                        : item.supervisorCompetencyNotMet === false
                                            ? "false"
                                            : "",
                                evaluatorInitials: item.evaluatorInitials || "",
                                evaluatorDate: parseIsoDateToMDYUS(item.evaluatorDate),
                            })),
                        }))
                        : initialSections;

                    const savedEmployeeName = (
                        data.name ||
                        data.applicantName ||
                        personFullName(data.user) ||
                        ""
                    ).trim();

                    methods.reset({
                        tenantId: tenantId || 0,
                        userId: data.userId || 1,
                        name: savedEmployeeName,
                        role: data.role || role,
                        dateOfEmployment: parseIsoDateToMDYUS(data.dateOfEmployment) || hireDate || "",
                        dateCompleted: parseIsoDateToMDYUS(data.dateCompleted) || "",
                        sections: remappedSections,
                        applicantName: savedEmployeeName,
                        applicantSignature: data.applicantSignature || "",
                        applicantSignedBy: data.applicantSignedBy || "",
                        applicantSignedAt: parseIsoDateToMDYUS(data.applicantSignedAt || data.applicantSignatureDate),
                        reviewedBySignature: data.reviewedBySignature || data.adminSignature || "",
                        reviewedBySignedAt: parseIsoDateToMDYUS(data.reviewedBySignedAt || data.reviewedBySignatureDate),
                        reviewedBySignedBy: data.reviewedBySignedBy || "",
                        applicantStatementAccepted: data.applicantStatementAccepted || false,
                        comments: data.comments || "",
                        status: data.status || "",
                    });
                }
            } catch (error) {
                console.error("Error fetching competency data:", error);
            }
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only form data load
    }, [targetUserId, currentUserData?.id, actualFromId]);


    const onSubmit = async (data, flag, additionalData = {}) => {

        try {
            const merged = mergeFormDataWithStatusExtras(data, additionalData);
            const employeeName = String(
                merged.name || merged.applicantName || "",
            ).trim();
            const payload = {
                tenantId: tenantId || 0,
                userId: Number.parseInt(merged.userId || targetUserId, 10) || 1,
                name: employeeName,
                applicantName: employeeName,
                role: role,
                dateOfEmployment: toIsoDate(merged.dateOfEmployment),
                dateCompleted: toIsoDate(merged.dateCompleted),
                competencyData: {
                    sections: merged.sections.map((s) => ({
                        sectionTitle: s.sectionTitle,
                        items: s.items.map((i) => {
                            const itemData = { ...i };

                            // Convert radio strings back to boolean for submission
                            if (itemData.hasExperience === "true")
                                itemData.hasExperience = true;
                            else if (itemData.hasExperience === "false")
                                itemData.hasExperience = false;
                            else if (itemData.hasExperience === "")
                                delete itemData.hasExperience;

                            if (itemData.isCompetent === "true") itemData.isCompetent = true;
                            else if (itemData.isCompetent === "false")
                                itemData.isCompetent = false;
                            else if (itemData.isCompetent === "") delete itemData.isCompetent;

                            // CLEANUP: Remove empty hhaSelfRating to avoid enum validation error
                            if (itemData.hhaSelfRating === "") {
                                delete itemData.hhaSelfRating;
                            }

                            // For HHA
                            if (itemData.supervisorCompetencyMet === "true") {
                                itemData.supervisorCompetencyMet = true;
                                itemData.supervisorCompetencyNotMet = false;
                            } else if (itemData.supervisorCompetencyMet === "false") {
                                itemData.supervisorCompetencyMet = false;
                                itemData.supervisorCompetencyNotMet = true;
                            }

                            // Cleanup based on role
                            if (role === "HHA") {
                                delete itemData.hasExperience;
                                delete itemData.isCompetent;
                                delete itemData.proficiencyRequired;
                                delete itemData.evaluationMethod;
                                delete itemData.preceptorInitials;
                                delete itemData.preceptorDate;
                                delete itemData.competency;
                            } else {
                                delete itemData.hhaSelfRating;
                                delete itemData.supervisorCompetencyMet;
                                delete itemData.supervisorCompetencyNotMet;
                                delete itemData.evaluatorInitials;
                                delete itemData.evaluatorDate;
                                delete itemData.skill;
                            }
                            // CLEANUP: dates
                            if (itemData.evaluatorDate) itemData.evaluatorDate = toIsoDate(itemData.evaluatorDate);
                            if (itemData.preceptorDate) itemData.preceptorDate = toIsoDate(itemData.preceptorDate);

                            return itemData;
                        }),
                    })),
                },
                applicantSignature: merged.applicantSignature,
                applicantSignedBy: merged.applicantSignedBy || employeeName,
                applicantSignedAt: toIsoDate(merged.applicantSignedAt),
                reviewedBySignature: merged.reviewedBySignature,
                reviewedBySignedAt: toIsoDate(merged.reviewedBySignedAt),
                reviewedBySignedBy: merged.reviewedBySignedBy || "",
                comments: merged.comments || "",
                applicantStatementAccepted: merged.applicantStatementAccepted || false,
                flag: flag,
            };

            toast.loading("Submitting Competency Assessment...", {
                id: "comp-submit",
            });

            const actualId = dataId || merged.id;
            const method = actualId ? "PUT" : "POST";
            const url = actualId ? `${apiEndpoint}/${actualId}` : apiEndpoint;
            const response = await request(url, {
                method,
                body: JSON.stringify(payload),
            });
        
            if (response.status === 1) {
                toast.success("Competency Assessment submitted successfully!", {
                    id: "comp-submit",
                });
                closeForm()
            } else {
                toast.error(response.message || "Failed to submit assessment!", {
                    id: "comp-submit",
                });
            }
        } catch (error) {
            console.error("Error submitting assessment:", error);
            toast.error(`Error: ${error.message}`, { id: "comp-submit" });
        }
    };

    const renderHhaRow = (sectionIdx, itemIdx) => {
        const path = `sections.${sectionIdx}.items.${itemIdx}`;
        const item = watch(path);
        return (
            <tr key={itemIdx} className="border-b hover:bg-gray-50/50">
                <td className="p-3 text-sm text-gray-700">{item.skill}</td>
                <td className="p-3 text-center">
                    <select
                        {...methods.register(`${path}.hhaSelfRating`)}
                        className="border rounded p-1 text-xs w-24"
                    >
                        <option value="">Select</option>
                        <option value="1">1 (None)</option>
                        <option value="2">2 (Review)</option>
                        <option value="3">3 (Indep)</option>
                    </select>
                </td>
                <td className="p-3 text-sm text-gray-600 text-center">{item.method}</td>
                <td className="p-3 text-center">
                    <div className="flex justify-center gap-2">
                        <label className="flex items-center gap-1 cursor-pointer">
                            <input
                                type="radio"
                                value="true"
                                {...methods.register(`${path}.supervisorCompetencyMet`)}
                                className="h-3 w-3"
                            />
                            <span className="text-[10px] font-bold text-green-600">YES</span>
                        </label>
                        <label className="flex items-center gap-1 cursor-pointer">
                            <input
                                type="radio"
                                value="false"
                                {...methods.register(`${path}.supervisorCompetencyMet`)}
                                className="h-3 w-3"
                            />
                            <span className="text-[10px] font-bold text-red-600">NO</span>
                        </label>
                    </div>
                </td>
                <td className="p-3">
                    <input
                        {...methods.register(`${path}.evaluatorInitials`)}
                        placeholder="Initials"
                        className="border rounded p-1 text-xs w-16 text-center"
                    />
                </td>
                <td className="p-3">
                    <InputDatePicker
                        formatSeparator="/"
                        name={`${path}.evaluatorDate`}
                        max={getLocalSystemDate()}
                        inputClassName="border rounded p-1 text-xs w-36"
                    />
                </td>
            </tr>
        );
    };

    const renderRnRow = (sectionIdx, itemIdx) => {
        const path = `sections.${sectionIdx}.items.${itemIdx}`;
        const item = watch(path);
        return (
            <tr key={itemIdx} className="border-b hover:bg-gray-50/50">
                <td className="p-3 text-center border-r bg-blue-50/10">
                    <div className="flex justify-center gap-3">
                        <label className="flex items-center gap-1 cursor-pointer">
                            <input
                                type="radio"
                                value="true"
                                {...methods.register(`${path}.hasExperience`)}
                                className="h-3 w-3"
                            />
                            <span className="text-[10px] uppercase font-bold text-gray-500">
                                Y
                            </span>
                        </label>
                        <label className="flex items-center gap-1 cursor-pointer">
                            <input
                                type="radio"
                                value="false"
                                {...methods.register(`${path}.hasExperience`)}
                                className="h-3 w-3"
                            />
                            <span className="text-[10px] uppercase font-bold text-gray-500">
                                N
                            </span>
                        </label>
                    </div>
                </td>
                <td className="p-3 text-center border-r bg-blue-50/10">
                    <div className="flex justify-center gap-3">
                        <label className="flex items-center gap-1 cursor-pointer">
                            <input
                                type="radio"
                                value="true"
                                {...methods.register(`${path}.isCompetent`)}
                                className="h-3 w-3"
                            />
                            <span className="text-[10px] uppercase font-bold text-gray-500">
                                Y
                            </span>
                        </label>
                        <label className="flex items-center gap-1 cursor-pointer">
                            <input
                                type="radio"
                                value="false"
                                {...methods.register(`${path}.isCompetent`)}
                                className="h-3 w-3"
                            />
                            <span className="text-[10px] uppercase font-bold text-gray-500">
                                N
                            </span>
                        </label>
                    </div>
                </td>
                <td className="p-3 text-sm text-gray-700">
                    {item.competency}
                    {item.proficiencyRequired && (
                        <span className="text-red-500 ml-1">*</span>
                    )}
                </td>
                <td className="p-3">
                    <input
                        {...methods.register(`${path}.evaluationMethod`)}
                        className="border rounded p-1 text-xs w-full"
                    />
                </td>
                <td className="p-3">
                    <input
                        {...methods.register(`${path}.preceptorInitials`)}
                        className="border rounded p-1 text-xs w-16 text-center"
                    />
                </td>
                <td className="p-3">
                    <InputDatePicker
                        formatSeparator="/"
                        name={`${path}.preceptorDate`}
                        max={getLocalSystemDate()}
                        inputClassName="border rounded p-1 text-xs w-36"
                    />
                </td>
            </tr>
        );
    };

    const renderSection = (item) => {
        // Find which sections in methods.watch('sections') belong to this 'item' (page section)
        const formSections = watch("sections");

        return (
            <div key={item.id} className="mb-10">
                <h4 className="font-bold text-gray-800 uppercase text-sm mb-4 border-l-4 border-blue-500 pl-3">
                    {item.title}
                </h4>
                {item.competencies.map((comp, compIdx) => {
                    const sectionTitle = comp.category || item.title;
                    const sectionIdx = formSections.findIndex(
                        (s) => s.sectionTitle === sectionTitle,
                    );
                    if (sectionIdx === -1) return null;

                    return (
                        <div
                            key={compIdx}
                            className="mb-6 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
                        >
                            {comp.category && (
                                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                                    <span className="text-xs font-bold text-gray-600">
                                        {comp.category}
                                    </span>
                                </div>
                            )}
                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse">
                                    <thead>
                                        {role === "HHA" ? (
                                            <tr className="bg-gray-50/50 border-b">
                                                <th className="p-3 text-left text-xs font-bold text-gray-600 uppercase w-[35%]">
                                                    Skill
                                                </th>
                                                <th className="p-3 text-center text-xs font-bold text-gray-600 uppercase">
                                                    Self Rating
                                                </th>
                                                <th className="p-3 text-center text-xs font-bold text-gray-600 uppercase">
                                                    Method
                                                </th>
                                                <th className="p-3 text-center text-xs font-bold text-gray-600 uppercase">
                                                    Met? (Y/N)
                                                </th>
                                                <th className="p-3 text-center text-xs font-bold text-gray-600 uppercase">
                                                    Initials
                                                </th>
                                                <th className="p-3 text-center text-xs font-bold text-gray-600 uppercase w-40">
                                                    Date
                                                </th>
                                            </tr>
                                        ) : (
                                            <>
                                                <tr className="bg-blue-50/30 border-b">
                                                    <th
                                                        colSpan="2"
                                                        className="p-2 text-center text-[10px] font-extrabold text-blue-900 border-r uppercase"
                                                    >
                                                        Self Assessment
                                                    </th>
                                                    <th
                                                        rowSpan="2"
                                                        className="p-3 text-left text-xs font-bold text-gray-600 uppercase w-[40%]"
                                                    >
                                                        {competencyForPositionLabel(role)}
                                                    </th>
                                                    <th
                                                        rowSpan="2"
                                                        className="p-3 text-center text-xs font-bold text-gray-600 uppercase"
                                                    >
                                                        Eval Method
                                                    </th>
                                                    <th
                                                        rowSpan="2"
                                                        className="p-3 text-center text-xs font-bold text-gray-600 uppercase"
                                                    >
                                                        Initials
                                                    </th>
                                                    <th
                                                        rowSpan="2"
                                                        className="p-3 text-center text-xs font-bold text-gray-600 uppercase w-40"
                                                    >
                                                        Date
                                                    </th>
                                                </tr>
                                                <tr className="bg-gray-50/50 border-b text-[8px]">
                                                    <th className="p-1 px-2 border-r text-gray-500 font-bold uppercase">
                                                        Exp? (Y/N)
                                                    </th>
                                                    <th className="p-1 px-2 border-r text-gray-500 font-bold uppercase">
                                                        Comp? (Y/N)
                                                    </th>
                                                </tr>
                                            </>
                                        )}
                                    </thead>
                                    <tbody>
                                        {formSections[sectionIdx].items.map((_, iIdx) =>
                                            role === "HHA"
                                                ? renderHhaRow(sectionIdx, iIdx)
                                                : renderRnRow(sectionIdx, iIdx),
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="max-w-7xl mx-auto bg-white shadow-2xl rounded-2xl overflow-hidden my-10 border border-gray-100">
            <EvaluationFormHeader
                title={
                    items.find((i) => i.type === "header")?.title ||
                    "COMPETENCY ASSESSMENT"
                }
                subtitle={items.find((i) => i.type === "header")?.subtitle || ""}
                onClose={closeForm}
            />

            <div className="p-8">
                <CorrectionRequestAlert status={currentStatus} comments={watch("comments")} />
                <FormProvider {...methods}>
                    <form className="space-y-8">
                        <fieldset disabled={isReadOnly} className="disabled:opacity-80 disabled:pointer-events-none">
                            {/* Header Item - Employee Details - Show only on page 1 */}
                            <div className="bg-blue-50/30 border border-blue-100 rounded-xl p-6 mb-8">
                                <h3 className="text-sm font-bold text-blue-900 uppercase tracking-wider mb-4 border-b border-blue-100 pb-2">
                                    Employee Information
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <EvaluationInput
                                        name="name"
                                        label="Employee Name"
                                        placeholder="Full Name"
                                    />
                                    <InputDatePicker
                                        formatSeparator="/"
                                        name="dateOfEmployment"
                                        label="Date of Employment"
                                        max={getLocalSystemDate()}
                                    />
                                </div>
                            </div>

                            {/* Page Content */}
                            <div className={`space-y-10 ${(page === totalPages) ? 'min-h-0' : 'min-h-[400px]'}`}>
                                {items
                                    .filter((item) => item.type === "section")
                                    .map((item, idx) => {
                                        const sectionItems = items.filter(
                                            (i) => i.type === "section",
                                        );
                                        const sectionCount = sectionItems.length;

                                        if (totalPages <= 3) {
                                            // For fewer pages, group sections
                                            const splitPoint = Math.ceil(sectionCount / 2);
                                            if (page === 1 && idx < splitPoint)
                                                return renderSection(item);
                                            if (page === 2 && idx >= splitPoint)
                                                return renderSection(item);
                                        } else {
                                            // For many pages, 1 section per page logic (simplified)
                                            // Adjust this logic if strict 1-to-1 page mapping is needed
                                            // -1 for signature page if applicable
                                            const itemsPerPage = Math.ceil(
                                                sectionCount / (totalPages - 1),
                                            );
                                            const startIndex = (page - 1) * itemsPerPage;
                                            const endIndex = startIndex + itemsPerPage;

                                            if (idx >= startIndex && idx < endIndex) {
                                                return renderSection(item);
                                            }
                                        }
                                        return null;
                                    })}
                            </div>
                        </fieldset>

                        <ApplicationFormFooter
                            page={page}
                            setPage={setPage}
                            totalPages={totalPages}
                            methods={methods}
                            onSave={handleSave}
                            onSubmit={(data, status, extra) => onSubmit(data, status, extra)}
                            userRole={currentUserData?.roleId || 1}
                            isSignatureModalOpen={isSignatureModalOpen}
                            openSignatureModal={openSignatureModal}
                            closeSignatureModal={closeSignatureModal}
                            handleSignatureApply={handleSignatureApply}
                            onStatusUpdate={handleStatusAction}
                            signerName={signerName}
                            isReadOnly={isReadOnly}
                            applicantName={watch("name") || watch("applicantName") || ""}
                            applicantSignature={watch('applicantSignature') || ""}
                            applicantSignedAt={watch('applicantSignedAt') || ""}
                            reviewedBySignedBy={watch('reviewedBySignedBy') || ""}
                            reviewedBySignature={watch('reviewedBySignature') || ""}
                            reviewedBySignedAt={watch('reviewedBySignedAt') || ""}
                            canSignEmployee={!isReadOnly && !isAdmin}
                            canSignAdmin={!isReadOnly && isAdmin}
                            onClose={closeForm}
                            currentStatus={currentStatus}
                        />
                    </form>
                </FormProvider>
            </div>
        </div>
    );
};

export default BaseCompetency;
